import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserCog, Plus, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = { email: "", password: "", name: "", branch_id: "" };

export default function Users() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const { data: branches = [] } = useQuery({ queryKey: ["branches"], queryFn: async () => (await api.get("/branches")).data });

  const createM = useMutation({
    mutationFn: (u) => api.post("/users", u),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); setOpen(false); setForm(empty); toast.success("Şube kullanıcısı oluşturuldu"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Oluşturulamadı"),
  });
  const delM = useMutation({ mutationFn: (id) => api.delete(`/users/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Kullanıcı silindi"); } });

  const submit = (e) => { e.preventDefault(); createM.mutate(form); };

  if (user?.role === "sube") return <Navigate to="/" replace />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <UserCog className="w-8 h-8 text-blue-400" /> Şube Kullanıcıları
          </h1>
          <p className="text-slate-400 text-sm mt-1">Her kullanıcı yalnızca kendi şubesinin verisini görür ve yönetir. Siz (yönetici) tümünü görürsünüz.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(empty); }}>
          <DialogTrigger asChild>
            <button data-testid="add-user-button" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors">
              <Plus className="w-4 h-4" /> Kullanıcı Ekle
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0D1220] border-slate-800 text-white">
            <DialogHeader><DialogTitle className="font-heading">Yeni Şube Kullanıcısı</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Şube</label>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger data-testid="user-branch-select" className="mt-1.5 bg-slate-900 border-slate-700"><SelectValue placeholder="Şube seç" /></SelectTrigger>
                  <SelectContent className="bg-[#0D1220] border-slate-700 text-white">
                    {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Ad / Etiket</label>
                <input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" placeholder="Örn. Kadıköy Müdürü" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">E-posta / Kullanıcı Adı</label>
                <input data-testid="user-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" placeholder="kadikoy@vilayet.com" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-400">Şifre</label>
                <input data-testid="user-password-input" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-blue-500" placeholder="En az 4 karakter" />
              </div>
              <DialogFooter>
                <button data-testid="user-submit-button" type="submit" disabled={createM.isPending || !form.branch_id} className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg text-sm">Oluştur</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-card-${u.id}`} className="glass rounded-2xl p-5">
            <div className="flex items-start justify-between">
              <div className="w-11 h-11 rounded-xl bg-blue-600/15 text-blue-400 flex items-center justify-center font-heading font-bold">{(u.name || u.email)[0].toUpperCase()}</div>
              <button data-testid={`delete-user-${u.id}`} onClick={() => delM.mutate(u.id)} className="text-slate-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="mt-4 text-sm font-semibold text-white">{u.name}</div>
            <div className="text-xs text-slate-400">{u.email}</div>
            <div className="text-xs text-purple-400 mt-2 flex items-center gap-1"><Building2 className="w-3 h-3" /> {u.branch_name}</div>
          </div>
        ))}
        {users.length === 0 && <div className="text-slate-500 text-sm py-12 text-center col-span-full">Henüz şube kullanıcısı yok. "Kullanıcı Ekle" ile oluşturun.</div>}
      </div>
    </div>
  );
}
